import $ from '@grins/fd-modules/jQuery';

import { Fandango_debugLog as debugLog } from '@grins/fd-modules/splits/onering-global';
import { Fandango as Fandango_context } from '@grins/fd-modules/splits/require-core';

type RegExRule = { regex: RegExp | string; msg: string };
type ValidatedFields = { [name: string]: unknown };
type ValidationError = { $input: any; error: string };

type Preset = {
  displayName: string;
  failRules?: RegExRule[];
  passRules?: RegExRule[];
  isRequired?: boolean;
};

type ValidationRule = {
  $input?: any;
  input?: Element | null;
  type?: string;
  displayName?: string;
  failRules?: RegExRule[];
  passRules?: RegExRule[];
  isRequired?: boolean;
  name?: string;
};

type ValidationResult = ValidatedFields & (| { hasErrors?: undefined; errors?: undefined } | {
  hasErrors: true;
  errors: ValidationError[]
});

let passwordRulesImported = false;

const { context: { passwordValidation } } = Fandango_context;

const ValidationTypes = {
  CUSTOM: 'none',
  NAMES: 'firstOrLastName',
  EMAIL: 'email',
  PASSWORD: 'password',
  ZIP: 'zipCode',
  PHONE: 'phoneNumber',
};

const RegExes = {
  SYMBOL_OR_NUMBER: /[0-9,!@#$%^&*()|?:;+=".<>]+/,
  VALID_EMAIL: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
  MSG_REPLACE: /{{name}}/gi,
};

const Presets: Record<string, Preset> = {
  [ValidationTypes.NAMES]: {
    displayName: 'Name',
    failRules: [{
      regex: RegExes.SYMBOL_OR_NUMBER,
      msg: '{{name}} cannot contain symbols or numbers.'
    }]
  },
  [ValidationTypes.EMAIL]: {
    displayName: 'Email',
    passRules: [{
      regex: RegExes.VALID_EMAIL,
      msg: 'Please enter a valid email address.'
    }]
  },
  [ValidationTypes.PASSWORD]: {
    displayName: 'Password',
  },
  [ValidationTypes.ZIP]: {
    displayName: 'Zip Code',
    passRules: [{
      regex: '^\\d{5}$|^\\d{5}-\\d{4}$',
      msg: 'Please enter a valid ZIP Code.'
    }]
  },
  [ValidationTypes.PHONE]: {
    displayName: 'Mobile Number',
    passRules: [{
      regex: '^(?:\\+1[\\s.\\-]?|1[\\s.\\-])?(?:\\()?\\d{3}\\)?[\\s.\\-]?\\d{3}[\\s.\\-]?\\d{4}$',
      msg: 'Please enter a valid Mobile Number.'
    }]
  },
};

function importPasswordRules(rules?: { regEx: RegExp | string; msg: string }[]) {
  if (!rules) {
    return debugLog.warn('shared/inputValidator', 'Password rules not found');
  }

  if (passwordRulesImported) {
    return;
  }

  passwordRulesImported = true;
  Presets[ValidationTypes.PASSWORD].failRules = rules.map(({ regEx, msg }) => ({ regex: regEx, msg }));
}

function applyRegExTests(value: string, tests: (RegExRule | undefined)[], displayName: string, successCondition: boolean) {
  return tests
    .filter((rule): rule is RegExRule => Boolean(rule && rule.regex && rule.msg))
    .reduce((error: string, { regex, msg }) => {
      if (error) {
        return error;
      }

      regex = typeof regex === 'string' ? new RegExp(regex, 'i') : regex;
      return regex.test(value) === successCondition ? msg.replace(RegExes.MSG_REPLACE, displayName) : '';
    }, '');
}

function normalizeValidationRule(rule: ValidationRule) {
  const $input = rule.$input || $(rule.input ?? {});
  const { type } = rule;
  // @ts-expect-error -- exists but deprecated
  let value = $.trim($input.val());

  if (type === ValidationTypes.EMAIL) {
    value = value.toLowerCase();
  }

  const preset: Partial<Preset> = Presets[type as string] || {};

  return {
    $input,
    displayName: rule.displayName || preset.displayName || '',
    failRules: ([] as (RegExRule | undefined)[]).concat(preset.failRules, rule.failRules),
    isRequired: Boolean(typeof rule.isRequired === 'boolean' ? rule.isRequired : preset.isRequired),
    name: rule.name || $input.attr('name') || `unnamed-${Math.round(9999 * Math.random())}`,
    passRules: ([] as (RegExRule | undefined)[]).concat(preset.passRules, rule.passRules),
    value,
  };
}

function validate(data: ValidationRule[]): ValidationResult {
  const errors: ValidationError[] = [];
  const results: ValidatedFields = {};
  importPasswordRules(passwordValidation && passwordValidation.rules);

  data.forEach((rule) => {
    const { $input, displayName, failRules, isRequired, name, passRules, value } = normalizeValidationRule(rule);

    if (isRequired && !value) {
      return errors.push({ $input, error: `${displayName} is required.` });
    }

    if (value) {
      let error: string;
      error = applyRegExTests(value, passRules, displayName, false);

      if (error) {
        return errors.push({ $input, error });
      }

      error = applyRegExTests(value, failRules, displayName, true);

      if (error) {
        return errors.push({ $input, error });
      }
    }

    results[name] = value;
  });

  if (errors.length) {
    Object.assign(results, { hasErrors: true, errors });
  }

  return results;
}

export default {
  ValidationTypes,
  validate,
};
